import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import * as api from '../../lib/api';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { DocTypeIcon } from '../../components/ui/DocTypeIcon';

const docTypeLabels = {
  will: 'Last Will & Testament',
  trust: 'Revocable Living Trust',
  immigration_poa: 'Special POA (Immigration)',
};

const ClientCard = ({ client, docs, onOpenDoc }) => {
  const sortedDocs = [...docs].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  const latestDoc = sortedDocs[0];
  const needsReview = docs.some((d) => d.status === 'in_review');

  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-brand-navy text-sm flex-shrink-0">
            {client.name.charAt(0)}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-brand-navy">{client.name}</h3>
              {needsReview && (
                <span className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded-full font-medium">
                  Action Required
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500">{client.email}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4 py-3 border-y border-slate-100">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <SafeIcon icon={FiIcons.FiFolder} className="text-slate-400" />
          <span className="font-semibold text-brand-navy">{docs.length}</span>
          <span>{docs.length === 1 ? 'Document' : 'Documents'}</span>
        </div>
        {latestDoc && (
          <div className="flex items-center gap-2 text-sm text-slate-600 border-l border-slate-200 pl-4">
            <span className="text-slate-400">Latest:</span>
            <StatusBadge status={latestDoc.status} />
          </div>
        )}
      </div>

      {docs.length === 0 ? (
        <p className="text-sm text-slate-400 italic text-center py-2">No documents started</p>
      ) : (
        <div className="space-y-2">
          {sortedDocs.map((doc) => (
            <button
              key={doc.id}
              onClick={() => onOpenDoc(doc.id)}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-brand-navy hover:bg-slate-50 transition-all text-left group"
            >
              <DocTypeIcon type={doc.type} className="text-base" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 group-hover:text-brand-navy truncate">
                  {docTypeLabels[doc.type]}
                </p>
                <p className="text-xs text-slate-400">Updated {new Date(doc.updated_at).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <StatusBadge status={doc.status} />
                <SafeIcon icon={FiIcons.FiChevronRight} className="text-slate-400 group-hover:text-brand-navy" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const AdvisorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => { loadData(); }, [user.id]);

  const loadData = async () => {
    setFetchError(null);
    setLoading(true);
    try {
      const clientsData = await api.getAdvisorClients(user.id);
      const docs = await api.getAdvisorDocuments(user.id);
      setClients(clientsData);
      setDocuments(docs);
    } catch (err) {
      console.error(err);
      setFetchError('Could not load portfolio data. Please try again.');
      setClients([]);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const getClientDocs = (clientId) => documents.filter((d) => d.user_id === clientId);

  const filteredClients = clients.filter(
    (c) => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalInReview = documents.filter((d) => d.status === 'in_review').length;

  if (loading) return <div className="p-8 text-center text-slate-500">Loading client portfolio...</div>;

  return (
    <div className="space-y-6">
      {fetchError && (
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg p-3">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{fetchError}</p>
        </div>
      )}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="page-title">Client Portfolio</h1>
          <p className="page-subtitle">Review and manage assigned client documents</p>
        </div>
        {totalInReview > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 rounded-lg text-sm font-medium w-full sm:w-auto">
            <SafeIcon icon={FiIcons.FiClock} />
            {totalInReview} document{totalInReview > 1 ? 's' : ''} awaiting review
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
        {[
          { label: 'Total Clients', value: clients.length, icon: FiIcons.FiUsers, color: 'text-blue-600 bg-blue-50' },
          { label: 'Total Documents', value: documents.length, icon: FiIcons.FiFolder, color: 'text-slate-600 bg-slate-100' },
          { label: 'In Review', value: totalInReview, icon: FiIcons.FiClock, color: 'text-amber-600 bg-amber-50' },
          { label: 'Finalized', value: documents.filter((d) => d.status === 'finalized').length, icon: FiIcons.FiCheckCircle, color: 'text-emerald-600 bg-emerald-50' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white border border-slate-200 rounded-xl p-3 md:p-4 flex items-center gap-3">
            <div className={`p-2 rounded-lg ${stat.color}`}>
              <SafeIcon icon={stat.icon} />
            </div>
            <div>
              <p className="text-2xl font-bold text-brand-navy">{stat.value}</p>
              <p className="text-xs text-slate-500">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative w-full max-w-md">
        <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search clients by name or email..."
          className="w-full pl-10 pr-4 py-2 rounded-md border border-slate-300 focus:ring-2 focus:ring-brand-navy outline-none bg-white"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Client Grid — single column on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {filteredClients.map((client) => (
          <ClientCard
            key={client.id}
            client={client}
            docs={getClientDocs(client.id)}
            onOpenDoc={(docId) => navigate(`/advisor/document/${docId}`)}
          />
        ))}
        {filteredClients.length === 0 && (
          <div className="col-span-full bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
            No clients found matching your search.
          </div>
        )}
      </div>
    </div>
  );
};